import { ReactNode, useEffect, useState } from "react";
// eslint-disable-next-line no-restricted-imports
import { IntercomProvider as ReactIntercomProvider } from "react-use-intercom";

import { serverApi } from "api/api-server-actions";
import { useAppSelector } from "store";
import {
  selectCurrentOrg,
  selectCurrentUserId,
  selectCurrentUserRole,
  userSelectors,
} from "store/selectors";
import { IntercomConfig } from "utils/intercom-types";
import { rollbarAndLogError } from "utils/logger";
import { isIntercomEnabled } from "utils/waffle-flags";

export const IntercomProvider = ({ children }: { children: ReactNode }) => {
  const userId = useAppSelector(selectCurrentUserId);
  const user = useAppSelector((s) => userSelectors.selectById(s, userId));
  const org = useAppSelector(selectCurrentOrg);
  const userRole = useAppSelector(selectCurrentUserRole);
  const [intercomConfig, setIntercomConfig] = useState<IntercomConfig | null>(null);

  // Fetch Intercom configuration when user and org are available
  useEffect(() => {
    const fetchIntercomConfig = async () => {
      if (!isIntercomEnabled() || !user || !org) {
        return;
      }

      try {
        const response = await serverApi.get<IntercomConfig>("/api/v1/intercom/data/");
        setIntercomConfig(response.data);
      } catch (error) {
        // Graceful fallback - just log the error and continue without Intercom
        rollbarAndLogError("Failed to fetch Intercom configuration", error);
      }
    };

    fetchIntercomConfig();
  }, [user, org]);

  return (
    <ReactIntercomProvider
      appId={intercomConfig?.app_id}
      autoBootProps={{
        userId: intercomConfig?.user_id,
        intercomUserJwt: intercomConfig?.intercom_user_jwt,
        customAttributes: {
          role: userRole,
          org: org?.name,
          environment: window._env_.ENV,
          product: "Forum V",
        },
      }}
      shouldInitialize={isIntercomEnabled() && !!intercomConfig}
      autoBoot
    >
      {children}
    </ReactIntercomProvider>
  );
};
